import { AgencyPerformanceSetting } from '@frontend/features/workflow-performance/Controls';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useState, useRef, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { Box, Tabs, Tab, useTheme, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { SettingsFields } from '@bandido/shared/types';
import { useGetSettingsByAgencyId, useUpdateAgencySettings } from '@frontend/features/agencies/queries';
import { Settings } from '@frontend/features/agencies/components/Settings';
import { Description, GppGood, Hotel, Map } from '@mui/icons-material';
import { useGetWorkflowsByAgency } from '@frontend/features/workflows/queries';
import { QueryKeys } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { createUploadAdapterPlugin } from '@frontend/features/common/helper';


export const GeneralSettings = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();
  const editorRef = useRef<any>(null);

  const updateSettingsMutation = useUpdateAgencySettings({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.getSettingsByAgencyId, Number(id), {}] });
      addToast({
        message: t('agencies.settings.saveSuccess'),
        color: 'success',
      });
    },
    onError: () => {
      addToast({
        message: t('agencies.settings.saveError'),
        color: 'error',
      });
    },
  });

  const { data: workflowsList } = useGetWorkflowsByAgency({
    pageSize: 1000,
    currentPage: 1,
    search: '',
    agencyId: id,
  });

  const { data: settings } = useGetSettingsByAgencyId({ id });

  const [selectedTab, setSelectedTab] = useState(0);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  if (!id) {
    return null;
  }

  const handleToast = useCallback(
    (message: string) => {
      addToast({
        message,
        color: 'error',
      });
    },
    [addToast],
  );

  return (
    <Box
      sx={{
        borderRadius: '24px',
        backgroundColor: theme.palette.common.white,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        alignSelf: 'stretch',
        height: 'calc(100vh - 180px)',
        overflow: 'hidden',
        padding: theme.spacing(3),
      }}
    >
      <Box width="100%">
        <Tabs value={selectedTab} onChange={handleChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<Description />} iconPosition="start" label={t('agencies.settings.general')} />
          <Tab icon={<GppGood />} iconPosition="start" label={t('agencies.settings.auth')} />
          <Tab icon={<Map />} iconPosition="start" label={t('agencies.settings.maps')} />
          <Tab icon={<Hotel />} iconPosition="start" label={t('agencies.settings.hotels')} />
        </Tabs>
        <OverlayScrollbarsComponent
          style={{
            marginTop: theme.spacing(5),
            boxSizing: 'border-box',
            width: '100%',
            overflowY: 'hidden',
            height: 'calc(100vh - 364px)',
          }}
          className="overlayscrollbars-react"
          options={{ scrollbars: { autoHide: 'leave', theme: 'os-theme-light' } }}
          defer
        >
          {selectedTab === 0 && (
            <Box display="flex" flexDirection="column" gap={5}>
              <AgencyPerformanceSetting agencyId={Number(id)} />
              <Divider />
              <Settings
                title={t('agencies.settings.whitelabeling')}
                agencyId={Number(id)}
                fields={[
                  SettingsFields.WHITELABEL_CODE,
                  SettingsFields.DEFAULT_WORKFLOW_ID,
                  SettingsFields.WHATSAPP_DEFAULT_WORKFLOW_ID,
                  SettingsFields.WHATSAPP_PREFERRED_AGENT_WORKFLOW_ID,
                ]}
                options={{
                  fieldTypes: [
                    {
                      id: SettingsFields.DEFAULT_WORKFLOW_ID,
                      type: 'select',
                      options: workflowsList
                        ? workflowsList.items.map((opt) => ({ label: opt.name, value: opt.id }))
                        : [],
                    },
                    {
                      id: SettingsFields.WHATSAPP_DEFAULT_WORKFLOW_ID,
                      type: 'select',
                      options: workflowsList
                        ? workflowsList.items.map((opt) => ({ label: opt.name, value: opt.id }))
                        : [],
                    },
                    {
                      id: SettingsFields.WHATSAPP_PREFERRED_AGENT_WORKFLOW_ID,
                      type: 'select',
                      options: workflowsList
                        ? workflowsList.items.map((opt) => ({ label: opt.name, value: opt.id }))
                        : [],
                    },
                  ],
                }}
                values={settings}
              />
              <Divider />
              <Settings
                title={t('agencies.settings.aws')}
                agencyId={Number(id)}
                fields={[
                  SettingsFields.AWS_REGION,
                  SettingsFields.AWS_S3_ENDPOINT,
                  SettingsFields.AWS_S3_BUCKET_EMAIL_ATTACHMENTS,
                  SettingsFields.AWS_ACCESS_KEY_ID,
                  SettingsFields.AWS_SECRET_ACCESS_KEY,
                ]}
                values={settings}
              />
              <Divider />

              <Settings
                title={t('agencies.settings.registration')}
                agencyId={Number(id)}
                fields={[SettingsFields.ALLOWED_EMAIL_DOMAIN, SettingsFields.VITE_MANUAL_REGISTRATION_ALLOWED]}
                values={settings}
              />

              <Divider />

              <Box display="flex" flexDirection="column" gap={2} maxWidth={700}>
                <Typography variant="h6" gutterBottom>
                  {t('agencies.settings.emailSignature')}
                </Typography>
                <Box>
                  <CKEditor
                    //@ts-ignore
                    editor={ClassicEditor}
                    data={settings?.[SettingsFields.EMAIL_SIGNATURE] || ''}
                    config={{
                      //@ts-ignore
                      extraPlugins: [createUploadAdapterPlugin(handleToast)],
                    }}
                    onReady={(editor) => {
                      editorRef.current = editor;
                    }}
                  />
                </Box>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  sx={{ width: 'fit-content', alignSelf: 'flex-end' }}
                  loading={updateSettingsMutation.isLoading}
                  onClick={() => {
                    if (!editorRef.current) return;

                    const editorData = editorRef.current.getData();
                    if (!editorData) return;

                    updateSettingsMutation.mutate({
                      agencyId: id as string,
                      settings: {
                        [SettingsFields.EMAIL_SIGNATURE]: editorData,
                      },
                    });
                  }}
                >
                  {t('common.save')}
                </LoadingButton>
              </Box>
            </Box>
          )}

          {selectedTab === 1 && (
            <Box display="flex" flexDirection="column" gap={5}>
              <Settings
                title={t('agencies.settings.googleSignIn')}
                agencyId={Number(id)}
                fields={[
                  SettingsFields.GOOGLE_CLIENT_ID,
                  SettingsFields.GOOGLE_CALLBACK_URL,
                  SettingsFields.GOOGLE_CLIENT_SECRET,
                  SettingsFields.CLIENT_URL,
                ]}
                values={settings}
              />
            </Box>
          )}

          {selectedTab === 2 && (
            <Box display="flex" flexDirection="column" gap={5}>
              <Settings
                title={t('agencies.settings.googleMaps')}
                agencyId={Number(id)}
                fields={[SettingsFields.VITE_MAP_ID, SettingsFields.VITE_GOOGLE_KEY]}
                values={settings}
              />
            </Box>
          )}

          {selectedTab === 3 && (
            <Box display="flex" flexDirection="column" gap={5}>
              <Settings
                title={t('agencies.settings.hotels')}
                agencyId={Number(id)}
                options={{
                  fieldTypes: [
                    {
                      id: SettingsFields.HOTEL_BOOK_AGENCY_REFERENCE,
                      type: 'textfield',
                      maxLength: 4,
                    },
                    {
                      id: SettingsFields.HOTEL_BOOK_MINOR_AGE_DELIMITER,
                      type: 'number',
                      maxLength: 2,
                    },
                  ],
                }}
                fields={[SettingsFields.HOTEL_BOOK_AGENCY_REFERENCE, SettingsFields.HOTEL_BOOK_MINOR_AGE_DELIMITER]}
                values={settings}
              />
            </Box>
          )}
        </OverlayScrollbarsComponent>
      </Box>
    </Box>
  );
};
